import { isNotInjected, RouteRow } from './routeModel';

/**
 * Cloud health of a route: what the reflector actually achieved in AWS, as
 * opposed to what BGP decided (CTX-323 / CTX-328).
 *
 * Two independent signals arrive per (prefix, route table):
 *   - `injectionStatus` — the reflector's own write result (injected / failed
 *     with the AWS error / withdrawn).
 *   - `awsState` — what the reconciliation sweep last observed in AWS
 *     (active / blackhole / missing / drift / table_missing).
 *
 * They can disagree, and that disagreement is the whole point: a route can be
 * BGP-selected, written successfully, and still be a **blackhole** in AWS
 * because the ENI behind it died. That case was invisible before this feature
 * (it caused a 1h21m silent outage) and is now a first-class state.
 *
 * Absence is NOT health: a reflector older than the feature sends neither
 * signal, so those rows resolve to `unknown` and must render as "no data"
 * rather than as a green light.
 *
 * A third signal rides the decision reason (CTX-738): when the rule's next hop
 * does not resolve to an ENI, naboo either reports the route table as never
 * injected (`not_injected`), or keeps the previous injection and says so
 * (`held`) — a held route used to read as healthy while BGP had moved on.
 */
export type CloudRouteHealthState =
  | 'healthy'
  | 'blackhole'
  | 'failed'
  | 'not_injected'
  | 'drift'
  | 'held'
  | 'withdrawn'
  | 'unverified'
  | 'injected'
  | 'unknown';

export interface CloudRouteHealth {
  state: CloudRouteHealthState;
  /** Short badge text. Empty for `unknown` — the caller renders a dash. */
  label: string;
  variant: 'success' | 'error' | 'warning' | 'neutral' | 'default';
  /** One-line explanation for a tooltip; includes the AWS error when there is one. */
  detail: string;
  /**
   * True when the sweep's verdict predates the last write, so `awsState`
   * describes a route object that has since been rewritten. The next sweep
   * pass refreshes it; until then the badge is shown but flagged.
   */
  stale: boolean;
}

/** Epoch seconds -> local string, matching how the table renders its other timestamps. */
const at = (epochSeconds: number | null): string =>
  epochSeconds == null ? 'an unknown time' : new Date(epochSeconds * 1000).toLocaleString();

/** Decision reasons naboo sets on an existing entry when its next hop stops resolving (CTX-738). */
const HELD_NEXT_HOP_UNRESOLVED = 'hold_nexthop_unresolved';
const WITHDRAWN_NEXT_HOP_UNRESOLVED = 'delete_nexthop_unresolved';

/**
 * Whether `injectionError` carries naboo's next-hop explanation rather than an AWS error. naboo
 * never overwrites a real write error with it, and a real write error always comes with
 * `failed`, so a failed row's error is AWS's.
 */
const carriesNextHopExplanation = (row: RouteRow): boolean =>
  isNotInjected(row) ||
  (row.injectionStatus !== 'failed' &&
    (row.decisionReason === HELD_NEXT_HOP_UNRESOLVED ||
      row.decisionReason === WITHDRAWN_NEXT_HOP_UNRESOLVED));

const nextHopExplanation = (row: RouteRow): string =>
  carriesNextHopExplanation(row) && row.injectionError
    ? row.injectionError
    : 'the next hop does not resolve to an ENI';

/**
 * What to say about the route's next hop, or null when there is nothing to say. Shared by the
 * health detail and the route detail panel so both word it the same way.
 */
export function describeNextHopIssue(row: RouteRow): string | null {
  if (isNotInjected(row)) {
    return `Not injected: ${nextHopExplanation(row)}. It is injected once the next hop resolves.`;
  }
  if (row.decisionReason === HELD_NEXT_HOP_UNRESOLVED) {
    return (
      `The current next hop does not resolve (${nextHopExplanation(row)}). ` +
      'The previous injection is kept because the rule has persistence on.'
    );
  }
  if (row.decisionReason === WITHDRAWN_NEXT_HOP_UNRESOLVED) {
    // Not "persistence off": the lab showed a rule WITH persistence landing here too, when an
    // earlier withdraw had left a tombstone and the prefix came back with a next hop that does
    // not resolve. What holds in every case is that AWS has no route for it.
    return `Not in AWS because the next hop does not resolve (${nextHopExplanation(row)}).`;
  }
  return null;
}

/** The AWS write error to show as such, or null when `injectionError` holds a next-hop explanation. */
export const cloudErrorOf = (row: RouteRow): string | null =>
  carriesNextHopExplanation(row) ? null : row.injectionError;

/**
 * Single source of truth for the badge, the tooltip and any filtering. Every
 * surface (table, detail panel, topology) must call this instead of
 * re-deriving the precedence, or the surfaces will disagree.
 *
 * Precedence, worst-first — a write that failed outranks any stored verdict,
 * and a blackhole outranks a stale "active":
 *   failed > blackhole > drift/missing > table not found > withdrawn > active > injected
 */
export function deriveCloudRouteHealth(row: RouteRow): CloudRouteHealth {
  const { injectionStatus, injectionAt, awsState, awsCheckedAt } = row;
  const injectionError = cloudErrorOf(row);
  // A withdrawn row is never held: there is nothing in AWS to hold on to.
  const held = row.decisionReason === HELD_NEXT_HOP_UNRESOLVED && !row.withdrawn;

  // A held route is worth a warning even from a reflector that has not reported its write yet.
  if (injectionStatus == null && awsState == null && !held) {
    return {
      state: 'unknown',
      label: '',
      variant: 'default',
      detail: 'This reflector does not report cloud injection state.',
      stale: false
    };
  }

  const stale =
    awsCheckedAt != null && injectionAt != null && awsCheckedAt < injectionAt;
  const checked = awsState == null ? '' : ` Last checked in AWS at ${at(awsCheckedAt)}.`;
  const staleNote = stale
    ? ' The check predates the last write, so it will refresh on the next verification pass.'
    : '';
  // Blackhole and drift outrank held, but the badge must not hide that the route is also being
  // held on a next hop that no longer resolves.
  const heldNote = held ? ` ${describeNextHopIssue(row)}` : '';

  if (injectionStatus === 'failed') {
    // A withdrawn route's failure is a failed DELETE, not a failed write: saying
    // "write failed" on a tombstone reads as if we tried to install it.
    const verb = row.withdrawn ? 'remove this route from' : 'write this route to';
    return {
      state: 'failed',
      label: row.withdrawn ? 'Withdraw failed' : 'Write failed',
      variant: 'error',
      detail: `The reflector could not ${verb} AWS${
        injectionError ? `: ${injectionError}` : '.'
      }`,
      stale: false
    };
  }

  if (isNotInjected(row)) {
    return {
      state: 'not_injected',
      label: 'Not injected',
      variant: 'error',
      detail: `${describeNextHopIssue(row)} Not in AWS since ${at(injectionAt)}.`,
      stale: false
    };
  }

  if (awsState === 'blackhole') {
    return {
      state: 'blackhole',
      label: 'Blackhole',
      variant: 'error',
      detail:
        'The route exists in AWS but its target is dead, so traffic is dropped. ' +
        `The reflector wrote it successfully at ${at(injectionAt)}.${checked}${staleNote}${heldNote}`,
      stale
    };
  }

  if (awsState === 'drift' || awsState === 'missing') {
    return {
      state: 'drift',
      label: awsState === 'missing' ? 'Missing' : 'Drift',
      variant: 'warning',
      detail:
        awsState === 'missing'
          ? `The route is not present in AWS even though the reflector wrote it at ${at(injectionAt)}.${checked}${heldNote}`
          : `The route in AWS points somewhere else than the reflector wrote at ${at(injectionAt)}.${checked}${heldNote}`,
      stale
    };
  }

  if (held) {
    // Below blackhole and drift on purpose: a held route that AWS also reports broken says the
    // worse thing first. Here AWS still has the previous write, so traffic follows it.
    return {
      state: 'held',
      label: 'Held',
      variant: 'warning',
      detail: `${describeNextHopIssue(row)}${checked}${staleNote}`,
      stale
    };
  }

  if (injectionStatus === 'withdrawn') {
    const why = describeNextHopIssue(row);
    return {
      state: 'withdrawn',
      label: 'Withdrawn',
      variant: 'neutral',
      detail: `The reflector removed this route from AWS at ${at(injectionAt)}.${why ? ` ${why}` : ''}`,
      stale: false
    };
  }

  if (awsState === 'active') {
    return {
      state: 'healthy',
      label: 'Active',
      variant: 'success',
      detail: `Written by the reflector and confirmed live in AWS.${checked}${staleNote}`,
      stale
    };
  }

  // The sweep could not read the route table back: a real anomaly, not a pass still pending.
  if (awsState === 'table_missing') {
    return {
      state: 'unverified',
      label: 'Table not found',
      variant: 'warning',
      detail: 'The reflector wrote this route but could not read its route table back from AWS.',
      stale: false
    };
  }

  // Written, and the sweep has not reached it yet (it runs every few minutes per member). This
  // says what is known, that the write went through, and no more: "Active" stays reserved for a
  // route AWS confirmed, since claiming health nobody verified is what hid the original incident.
  // A later pass that finds a problem still turns it into Blackhole / Drift / Missing.
  return {
    state: 'injected',
    label: 'Injected',
    variant: 'success',
    detail: `Written to AWS by the reflector at ${at(injectionAt)}. Its next verification pass confirms it in AWS.`,
    stale: false
  };
}

/**
 * Severity order, worst first. Exported so the table can sort by "how broken"
 * instead of alphabetically — the whole point of the column is that broken
 * routes surface first.
 */
export const CLOUD_HEALTH_SEVERITY: CloudRouteHealthState[] = [
  'failed',
  'not_injected',
  'blackhole',
  'drift',
  'held',
  'unverified',
  'withdrawn',
  'healthy',
  // After `healthy` on purpose: in a cluster group, a member that already confirmed the route in
  // AWS speaks for it over one whose sweep has not come round yet.
  'injected',
  'unknown'
];

/**
 * Worst state across a group of rows (the table groups by prefix, and a prefix
 * can span several route tables with different outcomes).
 *
 * Two rules, both about describing the prefix as it stands NOW:
 *  - Withdraw tombstones only speak when the whole group is withdrawn. A stale
 *    tombstone lingers next to a live announcement (they key on different route
 *    tables, so one never supersedes the other), and letting its failure win
 *    masked the live route's real state — observed in the lab, where a failed
 *    withdraw hid an actual blackhole on the same prefix.
 *  - `unknown` only wins when every row is unknown: one reporting row is enough
 *    to say something (e.g. the primary reports, the secondary never writes).
 */
export function worstCloudRouteHealth(rows: RouteRow[]): CloudRouteHealth {
  const order = CLOUD_HEALTH_SEVERITY;
  const live = rows.filter((r) => !r.withdrawn);
  const healths = (live.length > 0 ? live : rows).map(deriveCloudRouteHealth);
  const known = healths.filter((h) => h.state !== 'unknown');
  const pool = known.length > 0 ? known : healths;
  return pool.reduce((worst, h) =>
    order.indexOf(h.state) < order.indexOf(worst.state) ? h : worst
  );
}
